"use client";

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLenis } from "lenis/react";
import { 
  X, 
  Send, 
  User, 
  Mail, 
  Phone, 
  Building2, 
  MessageSquare, 
  ShieldCheck,
  CheckCircle2
} from "lucide-react";
import toast from "react-hot-toast";
import { useLanguage } from "@/context/LanguageContext";
import { contactService } from "@/services/contactService";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState({
    fullname: "",
    phone: "",
    email: "",
    businessName: "",
    message: "",
  });

  const lenis = useLenis();

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = "hidden";
      lenis?.stop();
    } else {
      document.body.style.overflow = "unset";
      lenis?.start();
    }
    return () => {
      document.body.style.overflow = "unset";
      lenis?.start();
    };
  }, [isOpen, lenis]);

  if (!isOpen || !mounted) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullname.trim()) {
      toast.error("Vui lòng nhập họ và tên");
      return;
    }
    if (!formData.phone.trim()) {
      toast.error("Vui lòng nhập số điện thoại");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Vui lòng nhập email");
      return;
    }
    if (!formData.businessName.trim()) {
      toast.error("Vui lòng nhập tên công ty/doanh nghiệp");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Vui lòng nhập tin nhắn của bạn");
      return;
    }
    setIsSubmitting(true);
    try {
      await contactService.submitContactForm(formData);
      setIsSubmitting(false);
      setIsSuccess(true);
      
      // Reset form
      setFormData({
        fullname: "",
        phone: "",
        email: "",
        businessName: "",
        message: "",
      });

      setTimeout(() => {
        setIsSuccess(false);
        onClose();
        toast.custom((t_toast) => (
          <div className={`${t_toast.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-[#0a192f] shadow-lg rounded-2xl pointer-events-auto flex ring-1 ring-[#00f3ff]/50 border border-[#00f3ff]/20`}>
            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <CheckCircle2 className="h-10 w-10 text-[#00f3ff] drop-shadow-[0_0_8px_#00f3ff]" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-black text-white uppercase tracking-wider">
                    {t("common.contact.success.title")}
                  </p>
                  <p className="mt-1 text-sm text-slate-400">
                    {t("common.contact.success.subtitle")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ));
      }, 2000);
    } catch (error: any) {
      setIsSubmitting(false);
      toast.error(error.message || "Failed to submit contact form");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto custom-scrollbar overscroll-contain" data-lenis-prevent>
      <div className="flex items-center justify-center min-h-full p-4 sm:p-6" data-lenis-prevent>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#040d21]/90 backdrop-blur-xl transition-opacity duration-500"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-[#0a192f]/40 backdrop-blur-2xl border border-[#00f3ff]/30 rounded-[3rem] shadow-[0_0_80px_rgba(0,243,255,0.2)] overflow-hidden animate-in fade-in zoom-in duration-500 flex flex-col">
        
        {/* Cyber Accents */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#00f3ff]/10 to-transparent pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-[#0066ff]/10 to-transparent pointer-events-none" />
        
        {/* Animated Scanning Line */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00f3ff] to-transparent opacity-20 animate-scan pointer-events-none" />

        {/* Glow Effects */}
        <div className="absolute top-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-[#00f3ff] to-transparent shadow-[0_0_15px_#00f3ff]" />
        <div className="absolute bottom-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-[#00f3ff] to-transparent shadow-[0_0_15px_#00f3ff]" />

        {/* Header */}
        <div className="px-10 py-10 border-b border-[#00f3ff]/10 flex items-center justify-between relative bg-[#00f3ff]/5 backdrop-blur-md">
          <div className="relative z-10 text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00f3ff]/10 border border-[#00f3ff]/20 text-[#00f3ff] text-[10px] font-black uppercase tracking-[0.2em] mb-4 shadow-[0_0_15px_rgba(0,243,255,0.1)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00f3ff] animate-pulse"></span>
              {t("common.contact.badge")}
            </div>
            <h3 className="text-4xl font-black text-white tracking-tighter leading-none mb-4">
              {t("common.contact.title")}{" "}
              <span className="text-[#00f3ff] drop-shadow-[0_0_12px_rgba(0,243,255,0.6)] italic">
                {t("common.contact.titleAccent")}
              </span>
            </h3>
            <p className="text-slate-400 text-sm max-w-md font-medium leading-relaxed opacity-80">
              {t("common.contact.subtitle")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-4 bg-white/5 hover:bg-[#00f3ff]/20 rounded-[1.5rem] transition-all duration-500 text-slate-400 hover:text-[#00f3ff] group active:scale-90 border border-white/5 hover:border-[#00f3ff]/30 shadow-inner"
          >
            <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-10 overflow-y-auto custom-scrollbar relative flex-1 overscroll-contain" data-lenis-prevent>
          {isSuccess ? (
            <div className="py-20 flex flex-col items-center text-center animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="w-24 h-24 bg-[#00f3ff]/10 rounded-[2rem] flex items-center justify-center mb-8 border border-[#00f3ff]/30 shadow-[0_0_50px_rgba(0,243,255,0.2)] rotate-12 transition-transform hover:rotate-0 duration-500">
                <Send className="w-10 h-10 text-[#00f3ff] drop-shadow-[0_0_8px_#00f3ff]" />
              </div>
              <h4 className="text-3xl font-black text-white mb-4 uppercase tracking-tighter">
                {t("common.contact.success.title")}
              </h4>
              <p className="text-slate-400 max-w-xs mx-auto font-medium leading-relaxed">
                {t("common.contact.success.subtitle")}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                {/* Name */}
                <div className="space-y-3 group">
                  <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                    {t("common.contact.form.name")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                    <input
                      type="text"
                      value={formData.fullname}
                      onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
                      placeholder={t("common.contact.form.placeholder.name")}
                      className="w-full pl-14 pr-6 py-5 bg-white/5 border border-white/10 rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-3 group text-left">
                  <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                    {t("common.contact.form.phone")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder={t("common.contact.form.placeholder.phone")}
                      className="w-full pl-14 pr-6 py-5 bg-white/5 border border-white/10 rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-3 group text-left">
                <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                  {t("common.contact.form.email")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t("common.contact.form.placeholder.email")}
                    className="w-full pl-14 pr-6 py-5 bg-white/5 border border-white/10 rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner"
                  />
                </div>
              </div>

              {/* Company */}
              <div className="space-y-3 group text-left">
                <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                  {t("common.contact.form.company")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                  <input
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    placeholder={t("common.contact.form.placeholder.company")}
                    className="w-full pl-14 pr-6 py-5 bg-white/5 border border-white/10 rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-3 group text-left">
                <label className="text-[10px] font-black text-[#00f3ff]/60 uppercase tracking-[0.2em] ml-2 group-focus-within:text-[#00f3ff] transition-colors">
                  {t("common.contact.form.message")} <span className="text-rose-500 font-black text-xs ml-1 drop-shadow-[0_0_8px_rgba(244,63,94,0.8)]">*</span>
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-5 top-6 text-slate-600 group-focus-within:text-[#00f3ff] transition-all group-focus-within:scale-110" size={18} />
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder={t("common.contact.form.placeholder.message")}
                    className="w-full pl-14 pr-6 py-5 bg-white/5 border border-white/10 rounded-[1.5rem] focus:outline-none focus:border-[#00f3ff]/50 focus:bg-[#00f3ff]/5 transition-all text-white placeholder:text-slate-700 font-bold text-sm tracking-tight shadow-inner resize-none overflow-hidden"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-6 bg-gradient-to-r from-[#00f3ff] to-[#0066ff] text-[#040d21] font-black text-xs uppercase tracking-[0.3em] rounded-[1.5rem] shadow-[0_0_30px_rgba(0,243,255,0.4)] hover:shadow-[0_0_50px_rgba(0,243,255,0.6)] hover:-translate-y-1.5 active:translate-y-0 transition-all duration-500 flex items-center justify-center gap-4 disabled:opacity-50 disabled:translate-y-0 group overflow-hidden relative"
              >
                {/* Button Shine Effect */}
                <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:left-full transition-all duration-1000 ease-in-out pointer-events-none" />
                
                {isSubmitting ? (
                  <div className="w-6 h-6 border-3 border-[#040d21]/30 border-t-[#040d21] rounded-full animate-spin" />
                ) : (
                  <>
                    {t("common.contact.form.submit")}
                    <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-10 py-6 bg-[#00f3ff]/5 border-t border-[#00f3ff]/10 text-center backdrop-blur-md">
          <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em] opacity-60">
            {t("common.contact.footer")}
          </p>
        </div>
      </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactModal;
